import { Button, Dropdown, MenuProps, Modal, Skeleton, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import { PlusOutlined, MoreOutlined, RightOutlined } from "@ant-design/icons";
import { Link, useHistory, useParams } from "react-router-dom";
import { CiqIconCircle } from "components/svg-icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  ErrorMessages,
  ProjectPermissionEnum,
  SuccessMessages
} from "constants/index";
import { QUERY_CUSTOM_REPORTS } from "../../gql/query";
import {
  MUTATION_CREATE_PROJECT_REPORT,
  MUTATION_DELETE_PROJECT_REPORT
} from "../../gql/mutation";
import { groupByOptions } from "../dataset-sidebar";
import LimitedTags from "../limited-tags";
import { CustomReportsI18n } from "../../custom-reports-constants";

type TFeature =
  | "MATERIALS"
  | "SUBMITTALS"
  | "DESIGN_PACKAGE"
  | "BID_PACKAGE"
  | "SCHEDULES";

type TReportDataCard = {
  id: string;
  name: string;
  query_configs: {
    features: {
      feature: TFeature;
    };
    description: string;
    name: string;
    additional_config: any;
    group_by_config: any;
    linked_entity_config: {
      display_type: string;
      only_governing_links: boolean;
    };
  };
};

type TReportCard = {
  id: string;
  name: string;
  description: string;
  feature: TFeature;
  tags: Array<{ tag: string; value: string }>;
};

const preventDefault = (e?: any) => {
  const domEvent = e?.domEvent || e;
  domEvent?.stopPropagation?.();
  domEvent?.preventDefault?.();
};

// const capitalizeFirstLetter = (val: string) => {
//   return val.charAt(0).toUpperCase() + String(val).slice(1).toLowerCase();
// };

export const buildLinkedEntityString = (linkedEntityConfig: any) => {
  const featureTypeIdToString: Record<number, string> = {
    1: "Submittals",
    9: "Activities"
  };

  const enabledLinks = linkedEntityConfig.filter(
    (entity: any) => entity.display_type !== "none"
  );

  if (enabledLinks.length === 0) return CustomReportsI18n.noLinks;

  const entityStringPrefix = "Linked ";

  return (
    entityStringPrefix +
    enabledLinks
      .map((entity: any) => featureTypeIdToString[entity.feature_type_id])
      .join(" & ")
  );
};

const translateToCustomReport = (reportData: TReportDataCard[]) => {
  return reportData.map((r) => {
    const tags: Array<{ tag: string; value: string }> = [];

    const linkedEntityString = buildLinkedEntityString(
      r?.query_configs?.linked_entity_config
    );

    // tags.push({
    //   tag: capitalizeFirstLetter(r.query_configs.features.feature),
    //   value: r.query_configs.features.feature
    // });

    tags.push({
      tag:
        groupByOptions.find(
          (x) => x.value === r.query_configs.group_by_config.field_keys[0]
        )?.tag || "No Grouping",
      value: r.query_configs.group_by_config.field_keys[0]
    });

    tags.push({
      tag: linkedEntityString,
      value: linkedEntityString
    });

    return {
      id: r.id,
      name: r.query_configs.name,
      description: r.query_configs.description,
      feature: r.query_configs.features.feature,
      tags
    } as TReportCard;
  });
};

function ReportSkeleton() {
  return (
    <div
      className="bg-white rounded-lg shadow px-4 py-3 flex justify-between items-center"
      data-testid="custom-report-card-skeleton"
    >
      <div className="flex items-start space-x-3 w-full">
        <Skeleton.Avatar active size="small" shape="circle" />
        <div className="flex-grow">
          <Skeleton
            active
            title={false}
            paragraph={{ rows: 2, width: ["80%", "60%"] }}
          />
          <div className="mt-2 flex gap-2">
            <Skeleton.Button active size="small" />
            <Skeleton.Button active size="small" />
            <Skeleton.Button active size="small" />
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomReportCard() {
  const history = useHistory();
  const { projectId } = useParams() as { projectId: string };
  const { gqlClientForProject, tokenContents, isProjectArchived } =
    useContext(ProjectContext);

  const canConfigureCustomReports = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanConfigureCustomReports,
    tokenContents?.role ?? ""
  );

  const [reports, setReports] = useState<Array<TReportCard>>();

  const {
    data: reportsData,
    loading: loadingReports,
    refetch: refetchReports
  } = useCIQQuery<{ project_reports: Array<TReportDataCard> }>(
    QUERY_CUSTOM_REPORTS,
    {
      skip: !gqlClientForProject,
      client: gqlClientForProject
    }
  );

  useEffect(() => {
    if (reportsData?.project_reports) {
      setReports(translateToCustomReport(reportsData.project_reports));
    }
  }, [reportsData?.project_reports]);

  const [createProjectReport, { loading: createLoading }] = useCIQMutation(
    MUTATION_CREATE_PROJECT_REPORT,
    { client: gqlClientForProject }
  );

  const [deleteProjectReport, { loading: deleteLoading }] = useCIQMutation(
    MUTATION_DELETE_PROJECT_REPORT,
    { client: gqlClientForProject }
  );

  const onOpenReport = (id: string) => {
    history.push(
      `/project/${projectId}/reports/project-reports/custom-report/${id}`
    );
  };

  const onCreateNewReport = async () => {
    if (!canConfigureCustomReports) return;

    const res = await createProjectReport({
      variables: { feature_type_id: 2 }
    });

    if (res.data?.create_custom_report) {
      onOpenReport(res.data.create_custom_report.id);
    } else {
      message.error(ErrorMessages.SomethingWentWrong);
    }
  };

  const onDeleteReport = useCallback(
    async (id: string) => {
      Modal.confirm({
        title: "Are you sure you want to delete this report?",
        okText: "Yes",
        cancelText: "No",
        async onOk() {
          if (!canConfigureCustomReports) return;

          const res = await deleteProjectReport({
            variables: { project_report_id: id }
          });

          if (res.data?.delete_custom_report) {
            message.success(
              res.data.delete_custom_report.message ||
                SuccessMessages.SuccessfullyUpdate
            );
            refetchReports();
          } else {
            message.error(ErrorMessages.SomethingWentWrong);
          }
        }
      });
    },
    [canConfigureCustomReports, deleteProjectReport, refetchReports]
  );

  const getMenu = (report: TReportCard): MenuProps => ({
    items: [
      {
        key: "export",
        label: "Export CSV",
        onClick: ({ domEvent }) => {
          preventDefault(domEvent);
        }
      },
      {
        key: "delete",
        disabled: !canConfigureCustomReports || deleteLoading,
        onClick: ({ domEvent }) => {
          preventDefault(domEvent);
          onDeleteReport(report.id);
        },
        label: (
          <span
            className={`text-red-500 ${
              deleteLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            title={
              !canConfigureCustomReports && !isProjectArchived
                ? ErrorMessages.PermissionNotGranted
                : ""
            }
          >
            {deleteLoading ? "Deleting..." : "Delete"}
          </span>
        )
      }
    ]
  });

  return (
    <div
      className="space-y-4 w-full max-w-full overflow-hidden"
      data-testid="custom-report-card-container"
    >
      {/* Header */}
      <div
        className="flex items-center pb-3"
        data-testid="custom-report-card-header"
      >
        <div
          className="grow pr-3"
          data-testid="custom-report-card-header-content"
        >
          <div className="flex items-center space-x-2">
            <div className="text-base font-semibold text-black text-opacity-90">
              Custom List Report
            </div>
            <div className="flex h-5 items-center justify-center border border-solid border-ciq-gray-300 rounded-xl px-2 py-0.5 text-black text-opacity-50 text-xs font-normal">
              {reports?.length} / 5
            </div>
          </div>
          <div className="text-black text-opacity-80 text-xs">
            Build your own Material List Report with advanced options of
            grouping, filtering and column display.
          </div>
        </div>

        <div className="w-[180px] text-right">
          <Tooltip
            title={reports?.length === 5 ? "Maximum 5 reports allowed" : ""}
            placement="top"
            className="pl-4"
          >
            <Button
              type="primary"
              onClick={() => onCreateNewReport()}
              disabled={
                !canConfigureCustomReports ||
                loadingReports ||
                reports?.length === 5
              }
              loading={createLoading}
              title={
                !canConfigureCustomReports && !isProjectArchived
                  ? "You don't have permission to create custom reports"
                  : ""
              }
              data-testid="custom-report-card-create-button"
            >
              <PlusOutlined className="text-white" /> Create New
            </Button>
          </Tooltip>
        </div>
      </div>

      {loadingReports ? (
        <div
          className="space-y-4"
          data-testid="custom-report-card-skeleton-list"
        >
          {[1, 2].map((i) => (
            <ReportSkeleton key={i} />
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid="custom-report-card-list">
          {reports?.map((report) => (
            <Link
              to={`/project/${projectId}/reports/project-reports/custom-report/${report.id}`}
              key={report.id}
              className="bg-white border border-solid border-ciq-gray-100 rounded-lg px-4 py-3 flex justify-between items-center w-full max-w-full"
              data-testid={`custom-report-card-${report.id}`}
            >
              {/* Left side */}
              <div className="flex items-start space-x-3 w-full max-w-full">
                <div className="pt-1">
                  <CiqIconCircle feature={report.feature} size={16} />
                </div>

                <div className="flex-grow flex-col overflow-hidden">
                  <div className="flex w-full justify-between">
                    <div className="flex flex-col overflow-hidden">
                      <div className="flex items-center font-semibold text-black text-opacity-90">
                        {report.name}
                      </div>
                      <div className="text-black text-opacity-50 max-w-full truncate">
                        {report.description || <span>&nbsp;</span>}
                      </div>
                    </div>

                    <Dropdown
                      menu={getMenu(report)}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <MoreOutlined
                        className="text-xl cursor-pointer text-gray-500 hover:text-gray-700"
                        onClick={preventDefault}
                      />
                    </Dropdown>
                  </div>

                  <div className="flex justify-between pr-1">
                    <div className="pt-3">
                      <LimitedTags
                        reportId={report.id}
                        tags={report.tags}
                        maxVisible={2}
                      />
                    </div>

                    <RightOutlined className="text-gray-400 cursor-pointer" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default CustomReportCard;
